import React from 'react';
import styled from 'styled-components';

import { ApiBalanceHistoryEvent } from 'api/types';
import SpinningRoundedBorder from 'components/basic/animations/SpinningRoundedBorder';
import LabelClickable from 'components/basic/Button/LabelClickable';
import MaterialSymbol from 'components/basic/icons/MaterialSymbol';
import Link from 'components/basic/Link/Link';
import { useSelectorActiveChain } from 'components/hooks';
import { StargateExplorerDoubleIcon } from 'components/wallet/icons/explorer/StargateExplorerDoubleIcon';
import { getBlockExplorerTransactionUrl } from 'config/configUtils';
import { useSelector } from 'reduxState/store';

import { convertApiBridge } from '../bridge/crossChainMapping';
import { getStargateScannerTxUrl } from '../bridge/utils';
import { getChainIcon } from '../chains/chainIcons/chainIcons';

import { PendingStatus } from './PendingStatus';

const Wrap = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 4px;

  /* Icons will inherit */
  font-size: 15px;
`;

/** Static width for all icons to make vertical alignement look good */
const IconWrap = styled.div`
  display: inline-flex;
  width: 15px;
  align-items: center;
  justify-items: center;
`;

const DoubleIconWrap = styled.div`
  display: inline-flex;
  width: 23px;
  align-items: center;
  justify-content: center;
`;

const TableIcon = styled.img`
  height: 12px;
  vertical-align: inherit;
`;

const SuccessIcon = () => (
  <MaterialSymbol
    icon="check_circle"
    fontSize="inherit"
    color="var(--green4)"
    filled
    rounded
  />
);

const WarningIcon = () => (
  <MaterialSymbol
    icon="warning"
    fontSize="inherit"
    color="var(--red3)"
    rounded
  />
);

export default function BalanceTransactionStatuses({
  transaction,
}: {
  transaction: ApiBalanceHistoryEvent;
}) {
  const isMobile = useSelector(state => state.screen.isMobile);
  const { activeChain } = useSelectorActiveChain();

  if (transaction.type === 'pendingDeposit') {
    return <PendingStatus transaction={transaction} />;
  }

  if (isMobile) {
    return (
      <Wrap>
        {transaction.type === 'withdrawal' && (
          <>
            <IconWrap>
              {transaction.katanaTxStatus === 'failed' ? (
                <WarningIcon />
              ) : transaction.katanaTxStatus === 'pending' ? (
                <SpinningRoundedBorder size={12} inline />
              ) : (
                <SuccessIcon />
              )}
            </IconWrap>
            <IconWrap>
              <TableIcon src={getChainIcon('katanaChain')} />
            </IconWrap>
            {transaction.bridgeTarget !== 'katana.katana' &&
              transaction.katanaTxId && (
                <DoubleIconWrap>
                  <StargateExplorerDoubleIcon
                    chain="katanaChain"
                    chainType="target"
                    size={12}
                  />
                </DoubleIconWrap>
              )}
          </>
        )}

        {transaction.type === 'deposit' && (
          <>
            {convertApiBridge(transaction.bridgeSource).bridge ===
              'Stargate' && (
              <DoubleIconWrap>
                <StargateExplorerDoubleIcon
                  chain={convertApiBridge(transaction.bridgeSource).targetChain}
                  chainType="source"
                  size={12}
                />
              </DoubleIconWrap>
            )}
            {transaction.forwarderTxId && (
              <DoubleIconWrap>
                <StargateExplorerDoubleIcon
                  chain="ethereum"
                  chainType="source"
                  size={12}
                />
              </DoubleIconWrap>
            )}
            <IconWrap>
              <TableIcon src={getChainIcon('katanaChain')} />
            </IconWrap>
            {transaction.bridgeTxId || transaction.katanaTxId ? (
              <IconWrap>
                <SuccessIcon />
              </IconWrap>
            ) : (
              <LabelClickable type="error">Pending</LabelClickable>
            )}
          </>
        )}
      </Wrap>
    );
  }

  return (
    <Wrap>
      {transaction.type === 'withdrawal' && (
        <>
          <IconWrap>
            {transaction.katanaTxStatus === 'failed' ? (
              <WarningIcon />
            ) : transaction.katanaTxStatus === 'pending' ? (
              <SpinningRoundedBorder size={12} inline />
            ) : (
              <SuccessIcon />
            )}
          </IconWrap>

          {transaction.katanaTxId ? (
            <IconWrap>
              <Link
                href={getBlockExplorerTransactionUrl(
                  transaction.katanaTxId,
                  activeChain
                )}
                $alignCenter
              >
                <TableIcon src={getChainIcon('katanaChain')} />
              </Link>
            </IconWrap>
          ) : null}

          {transaction.bridgeTarget !== 'katana.katana' &&
            transaction.katanaTxId && (
              <DoubleIconWrap>
                <Link
                  href={getStargateScannerTxUrl(transaction.katanaTxId)}
                  $alignCenter
                >
                  <StargateExplorerDoubleIcon
                    chain="ethereum"
                    chainType="target"
                    size={12}
                  />
                  {/* <img src={stargateBridgeIcon} /> */}
                </Link>
              </DoubleIconWrap>
            )}
        </>
      )}

      {transaction.type === 'deposit' && (
        <>
          {transaction.bridgeTxId && (
            <DoubleIconWrap>
              <Link
                href={getStargateScannerTxUrl(transaction.bridgeTxId)}
                $alignCenter
              >
                <StargateExplorerDoubleIcon
                  chainType="source"
                  chain={convertApiBridge(transaction.bridgeSource).targetChain}
                  size={12}
                />
              </Link>
            </DoubleIconWrap>
          )}

          {transaction.forwarderTxId && (
            <DoubleIconWrap>
              <Link
                href={getStargateScannerTxUrl(transaction.forwarderTxId)}
                $alignCenter
              >
                <StargateExplorerDoubleIcon
                  chain="ethereum"
                  chainType="source"
                  size={12}
                />
              </Link>
            </DoubleIconWrap>
          )}

          <IconWrap>
            <Link
              href={getBlockExplorerTransactionUrl(
                transaction.katanaTxId,
                activeChain
              )}
              $alignCenter
            >
              <TableIcon src={getChainIcon('katanaChain')} />
            </Link>
          </IconWrap>

          {transaction.katanaTxId ? (
            <IconWrap>
              <SuccessIcon />
            </IconWrap>
          ) : (
            <LabelClickable
              type="error"
              // onClick={txLink ? () => openExternalUrl(txLink) : undefined}
            >
              Pending
            </LabelClickable>
          )}
        </>
      )}
    </Wrap>
  );
}
